import type { OAuthRedirectMetadata } from "api-contracts/rpc/social-auth";
import { type ReactiveController, type ReactiveControllerHost } from "lit";
import { socialAuthService } from "../social-auth";

export interface SocialAuthState {
  isLoading: boolean;
  error?: string;
  isAuthenticated: boolean;
}

export class SocialAuthController implements ReactiveController {
  private host: ReactiveControllerHost;
  private _state: SocialAuthState = {
    isLoading: false,
    error: undefined,
    isAuthenticated: false,
  };

  constructor(host: ReactiveControllerHost) {
    this.host = host;
    this.host.addController(this);
  }

  hostConnected(): void {
    // Listen for social auth events
    window.addEventListener("social-auth-success", this.handleAuthSuccess as EventListener);
    window.addEventListener("social-auth-error", this.handleAuthError as EventListener);
  }

  hostDisconnected(): void {
    window.removeEventListener("social-auth-success", this.handleAuthSuccess as EventListener);
    window.removeEventListener("social-auth-error", this.handleAuthError as EventListener);
  }

  get state(): SocialAuthState {
    return this._state;
  }

  private handleAuthSuccess = () => {
    this._state = {
      ...this._state,
      isLoading: false,
      error: undefined,
      isAuthenticated: true,
    };
    this.host.requestUpdate();
  };

  private handleAuthError = (event: Event) => {
    const customEvent = event as CustomEvent;
    this._state = {
      ...this._state,
      isLoading: false,
      error: customEvent.detail.error,
      isAuthenticated: false,
    };
    this.host.requestUpdate();
  };

  async authenticateWith(
    provider: "google" | "facebook" | "apple",
    redirectMetadata?: OAuthRedirectMetadata,
  ): Promise<void> {
    if (this._state.isLoading) {
      return; // Prevent multiple concurrent auth attempts
    }

    this._state = {
      ...this._state,
      isLoading: true,
      error: undefined,
    };
    this.host.requestUpdate();

    try {
      await socialAuthService.initiateOAuth(provider, redirectMetadata);
    } catch (error) {
      this._state = {
        ...this._state,
        isLoading: false,
        error: error instanceof Error ? error.message : "Authentication failed",
      };
      this.host.requestUpdate();
    }
  }

  async authenticateWithToken(provider: "google" | "facebook" | "apple", token: string): Promise<void> {
    if (this._state.isLoading) {
      return;
    }

    this._state = {
      ...this._state,
      isLoading: true,
      error: undefined,
    };
    this.host.requestUpdate();

    try {
      await socialAuthService.authenticateWithToken(provider, token);
    } catch (error) {
      this._state = {
        ...this._state,
        isLoading: false,
        error: error instanceof Error ? error.message : "Authentication failed",
      };
      this.host.requestUpdate();
    }
  }

  clearError(): void {
    this._state = {
      ...this._state,
      error: undefined,
    };
    this.host.requestUpdate();
  }

  reset(): void {
    this._state = {
      isLoading: false,
      error: undefined,
      isAuthenticated: false,
    };
    this.host.requestUpdate();
  }
}
